import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import {
  DEFAULT_MEMBERS_LIST_STATE,
  parseMembersListState,
  serializeMembersListState,
  type MembersListState,
} from './members-list-state';

export const useMembersListState = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = parseMembersListState(searchParams);

  const patchState = useCallback(
    (patch: Partial<MembersListState>, options?: { replace?: boolean }) => {
      const next: MembersListState = {
        ...state,
        ...patch,
      };
      const params = serializeMembersListState(next, { includeSelected: true });
      setSearchParams(params, { replace: options?.replace ?? false });
    },
    [setSearchParams, state],
  );

  const replaceState = useCallback(
    (next: MembersListState, options?: { replace?: boolean }) => {
      const params = serializeMembersListState(next, { includeSelected: true });
      setSearchParams(params, { replace: options?.replace ?? false });
    },
    [setSearchParams],
  );

  const resetFilters = useCallback(() => {
    replaceState({
      ...DEFAULT_MEMBERS_LIST_STATE,
      q: state.q,
      page: 1,
      selectedId: state.selectedId,
    });
  }, [replaceState, state.q, state.selectedId]);

  return {
    state,
    patchState,
    replaceState,
    resetFilters,
  };
};

export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(query).matches : false,
  );

  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    onChange();
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);

  return matches;
};
